import * as Haptics from "expo-haptics";
import { ReactNode, useState } from "react";
import { Link, useLocalSearchParams } from "expo-router";
import { Edit3, Link2, Mail, Save, ShieldCheck } from "lucide-react-native";
import { Pressable, StyleSheet, Text, TextInput, View } from "react-native";

import { PageShell, SectionIntro } from "../../src/components/layout";
import { Reveal } from "../../src/components/reveal";
import { Button, Panel, Stat } from "../../src/components/ui";
import { formatCompactDate } from "../../src/lib/format";
import { staffPath } from "../../src/lib/hosting";
import { palette, radii, spacing } from "../../src/theme/tokens";
import { useAppStore } from "../../src/providers/AppProvider";

export default function FamilyViewScreen() {
  const { memorialId } = useLocalSearchParams<{ memorialId: string }>();
  const { getMemorial, getMemorialEvents, data, updateMemorialNotes, updateEventNotes } = useAppStore();
  const [editingQuote, setEditingQuote] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [draftQuote, setDraftQuote] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [draftInstructions, setDraftInstructions] = useState("");
  const [draftParking, setDraftParking] = useState("");
  const [draftDress, setDraftDress] = useState("");
  const memorial = memorialId ? getMemorial(memorialId) : undefined;
  const events = memorial ? getMemorialEvents(memorial.id) : [];
  const attending = data.rsvps.filter((entry) => events.some((event) => event.id === entry.eventId)).length;

  if (!memorial) {
    return (
      <PageShell>
        <SectionIntro
          eyebrow="Family View"
          title="This memorial is not available."
          description="The family-facing route needs a valid memorial identifier."
        />
      </PageShell>
    );
  }

  return (
    <PageShell role="family">
      <View style={styles.content}>
        <Reveal>
          <SectionIntro
            eyebrow="Scoped Family Access"
            title={`A calmer overview for ${memorial.decedentName}.`}
            description="This view keeps the family informed without exposing internal staff operations, registry management, or sensitive attendee controls."
          />
        </Reveal>

        <Reveal delay={90}>
          <View style={styles.statRow}>
            <Stat value={String(events.length)} label="Published events" accent="cedar" />
            <Stat value={String(attending)} label="Responses received" accent="forest" />
          </View>
        </Reveal>

        <Reveal delay={140}>
          <Panel style={styles.editPanel}>
            <View style={styles.editHeader}>
              <Edit3 color={palette.gold} size={18} strokeWidth={1.8} />
              <Text style={styles.sectionTitle}>Customize Memorial</Text>
            </View>

            <View style={styles.editField}>
              <Text style={styles.editLabel}>Hero Quote</Text>
              {editingQuote ? (
                <View style={styles.editInputWrap}>
                  <TextInput
                    style={styles.editInput}
                    value={draftQuote}
                    onChangeText={setDraftQuote}
                    multiline
                    placeholder="A meaningful quote..."
                    placeholderTextColor={palette.mist}
                  />
                  <Pressable
                    style={styles.saveButton}
                    onPress={() => {
                      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                      updateMemorialNotes(memorial.id, { heroQuote: draftQuote });
                      setEditingQuote(false);
                    }}
                  >
                    <Save color={palette.white} size={14} strokeWidth={2} />
                    <Text style={styles.saveLabel}>Save</Text>
                  </Pressable>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    setDraftQuote(memorial.heroQuote);
                    setEditingQuote(true);
                  }}
                >
                  <Text style={styles.editValue}>{memorial.heroQuote || "Tap to add a quote"}</Text>
                </Pressable>
              )}
            </View>

            <View style={styles.editField}>
              <Text style={styles.editLabel}>Family Note</Text>
              {editingNotes ? (
                <View style={styles.editInputWrap}>
                  <TextInput
                    style={styles.editInput}
                    value={draftNotes}
                    onChangeText={setDraftNotes}
                    multiline
                    placeholder="A note for guests..."
                    placeholderTextColor={palette.mist}
                  />
                  <Pressable
                    style={styles.saveButton}
                    onPress={() => {
                      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                      updateMemorialNotes(memorial.id, { editableNotes: draftNotes });
                      setEditingNotes(false);
                    }}
                  >
                    <Save color={palette.white} size={14} strokeWidth={2} />
                    <Text style={styles.saveLabel}>Save</Text>
                  </Pressable>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    setDraftNotes(memorial.editableNotes ?? "");
                    setEditingNotes(true);
                  }}
                >
                  <Text style={styles.editValue}>{memorial.editableNotes || "Tap to add a note for guests"}</Text>
                </Pressable>
              )}
            </View>
          </Panel>
        </Reveal>

        <Reveal delay={160}>
          <View style={styles.grid}>
            <Panel spotlight style={styles.primaryPanel}>
              <Text style={styles.sectionTitle}>What the family can do</Text>
              <View style={styles.featureList}>
                <FeatureRow
                  icon={<Link2 color={palette.cedar} size={18} strokeWidth={1.8} />}
                  text="Share memorial and event links with guests"
                />
                <FeatureRow
                  icon={<Mail color={palette.gold} size={18} strokeWidth={1.8} />}
                  text="Preview service communication windows without touching delivery tools"
                />
                <FeatureRow
                  icon={<ShieldCheck color={palette.forest} size={18} strokeWidth={1.8} />}
                  text="Stay informed while staff-only registry and check-in controls remain protected"
                />
              </View>
              <Link href={staffPath("/admin") as never} asChild>
                <Button label="Return to Admin View" variant="secondary" />
              </Link>
            </Panel>

            <Panel style={styles.secondaryPanel}>
              <Text style={styles.sectionTitle}>Memorial timeline</Text>
              <View style={styles.timeline}>
                {events.map((event) => (
                  <View key={event.id} style={styles.timelineItem}>
                    <Text style={styles.timelineDate}>{formatCompactDate(event.startAt)}</Text>
                    <Text style={styles.timelineTitle}>{event.title}</Text>
                    <Text style={styles.timelineMeta}>{event.venue}</Text>
                    <Text style={styles.timelineMeta}>{event.specialInstructions ?? "No special note"}</Text>
                    {editingEventId === event.id ? (
                      <View style={styles.eventEditWrap}>
                        <TextInput
                          style={styles.editInput}
                          value={draftInstructions}
                          onChangeText={setDraftInstructions}
                          placeholder="Special instructions..."
                          placeholderTextColor={palette.mist}
                        />
                        <TextInput
                          style={styles.editInput}
                          value={draftParking}
                          onChangeText={setDraftParking}
                          placeholder="Parking notes..."
                          placeholderTextColor={palette.mist}
                        />
                        <TextInput
                          style={styles.editInput}
                          value={draftDress}
                          onChangeText={setDraftDress}
                          placeholder="Dress code notes..."
                          placeholderTextColor={palette.mist}
                        />
                        <View style={styles.eventEditActions}>
                          <Pressable
                            style={styles.saveButton}
                            onPress={() => {
                              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                              updateEventNotes(event.id, {
                                specialInstructions: draftInstructions || undefined,
                                parkingNotes: draftParking || undefined,
                                dressNotes: draftDress || undefined,
                              });
                              setEditingEventId(null);
                            }}
                          >
                            <Save color={palette.white} size={14} strokeWidth={2} />
                            <Text style={styles.saveLabel}>Save</Text>
                          </Pressable>
                          <Pressable onPress={() => setEditingEventId(null)}>
                            <Text style={styles.cancelLabel}>Cancel</Text>
                          </Pressable>
                        </View>
                      </View>
                    ) : (
                      <Pressable
                        onPress={() => {
                          setEditingEventId(event.id);
                          setDraftInstructions(event.specialInstructions ?? "");
                          setDraftParking(event.parkingNotes ?? "");
                          setDraftDress(event.dressNotes ?? "");
                        }}
                      >
                        <Text style={styles.editLink}>Edit details</Text>
                      </Pressable>
                    )}
                  </View>
                ))}
              </View>
            </Panel>
          </View>
        </Reveal>
      </View>
    </PageShell>
  );
}

function FeatureRow({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <View style={styles.featureRow}>
      {icon}
      <Text style={styles.featureText}>{text}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    gap: spacing.xl,
    paddingBottom: spacing.xxl,
  },
  statRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 18,
    alignItems: "flex-start",
  },
  primaryPanel: {
    flexBasis: 420,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    gap: 16,
  },
  secondaryPanel: {
    flexBasis: 360,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    gap: 16,
  },
  sectionTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 30,
    lineHeight: 32,
    textTransform: "capitalize",
  },
  featureList: {
    gap: 14,
  },
  featureRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
  },
  featureText: {
    flex: 1,
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  timeline: {
    gap: 14,
  },
  timelineItem: {
    width: "100%",
    minWidth: 0,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: palette.line,
    backgroundColor: "rgba(255,255,255,0.58)",
    padding: 16,
    gap: 5,
  },
  timelineDate: {
    color: palette.cedar,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  timelineTitle: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 25,
    lineHeight: 27,
  },
  timelineMeta: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  editPanel: {
    gap: 16,
  },
  editHeader: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  editField: {
    gap: 6,
  },
  editLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  editValue: {
    color: palette.ink,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  editInputWrap: {
    gap: 8,
  },
  editInput: {
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: palette.line,
    backgroundColor: palette.white,
    padding: 12,
    color: palette.ink,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    backgroundColor: palette.forest,
    borderRadius: radii.pill,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  saveLabel: {
    color: palette.white,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
  },
  eventEditWrap: {
    gap: 8,
    marginTop: 8,
  },
  eventEditActions: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  cancelLabel: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
  },
  editLink: {
    color: palette.cedar,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    marginTop: 4,
  },
});
